import {
  type AIPhoneLineTypeEnum,
  type PhonicBackgroundNoise,
  type PhonicPronunciationDictionaryEntry,
} from '~types'

export interface ElevenLabsVoiceSettings {
  voiceId: string | null
  speed: number | null
  stability: number | null
  similarity: number | null
}

export const DEFAULT_PHONE_LINE_TYPE: AIPhoneLineTypeEnum = 'PHONIC'
export const DEFAULT_PHONIC_VOICE_ID: string = 'virginia'

export const DEFAULT_ELEVEN_LABS_VOICE_SETTINGS: ElevenLabsVoiceSettings = {
  voiceId: null,
  speed: null,
  stability: null,
  similarity: null,
}

export interface PhonicVoiceSettings {
  voiceId: string | null
  audioSpeed: number
  isWelcomeMessageInterruptible: boolean
  minWordsToInterrupt: number
  enableAssistantBackchannel: boolean
  assistantBackchannelAggressiveness: number
  backgroundNoise: PhonicBackgroundNoise | null
  backgroundNoiseLevel: number
  boostedKeywords: string[]
  pronunciationDictionary: PhonicPronunciationDictionaryEntry[]
}

export const DEFAULT_PHONIC_VOICE_SETTINGS: PhonicVoiceSettings = {
  voiceId: DEFAULT_PHONIC_VOICE_ID,
  audioSpeed: 1.2,
  isWelcomeMessageInterruptible: true,
  minWordsToInterrupt: 1,
  enableAssistantBackchannel: false,
  assistantBackchannelAggressiveness: 0.1,
  backgroundNoise: null,
  backgroundNoiseLevel: 0,
  boostedKeywords: [],
  pronunciationDictionary: [],
}

export const phonicSettingsFromMetadata = (metadata: unknown): PhonicVoiceSettings => {
  const phonic = (metadata as Record<string, Record<string, unknown>> | null)?.phonic ?? {}
  const defaults = DEFAULT_PHONIC_VOICE_SETTINGS
  return {
    voiceId: (phonic.voice_id as string | null) ?? defaults.voiceId,
    audioSpeed: (phonic.audio_speed as number) ?? defaults.audioSpeed,
    isWelcomeMessageInterruptible:
      (phonic.is_welcome_message_interruptible as boolean) ??
      defaults.isWelcomeMessageInterruptible,
    minWordsToInterrupt: (phonic.min_words_to_interrupt as number) ?? defaults.minWordsToInterrupt,
    enableAssistantBackchannel:
      (phonic.enable_assistant_backchannel as boolean) ?? defaults.enableAssistantBackchannel,
    assistantBackchannelAggressiveness:
      (phonic.assistant_backchannel_aggressiveness as number) ??
      defaults.assistantBackchannelAggressiveness,
    backgroundNoise:
      (phonic.background_noise as PhonicBackgroundNoise | null) ?? defaults.backgroundNoise,
    backgroundNoiseLevel:
      (phonic.background_noise_level as number) ?? defaults.backgroundNoiseLevel,
    boostedKeywords: (phonic.boosted_keywords as string[]) ?? defaults.boostedKeywords,
    pronunciationDictionary:
      (phonic.pronunciation_dictionary as PhonicPronunciationDictionaryEntry[]) ??
      defaults.pronunciationDictionary,
  }
}

export const phonicSettingsToRequestParams = (
  settings: PhonicVoiceSettings,
): {
  phonic_voice_id: string | null
  phonic_audio_speed: number
  phonic_is_welcome_message_interruptible: boolean
  phonic_min_words_to_interrupt: number
  phonic_enable_assistant_backchannel: boolean
  phonic_assistant_backchannel_aggressiveness: number
  phonic_background_noise: PhonicBackgroundNoise | null
  phonic_background_noise_level: number
  phonic_boosted_keywords: string[]
  phonic_pronunciation_dictionary: PhonicPronunciationDictionaryEntry[]
} => ({
  phonic_voice_id: settings.voiceId,
  phonic_audio_speed: settings.audioSpeed,
  phonic_is_welcome_message_interruptible: settings.isWelcomeMessageInterruptible,
  phonic_min_words_to_interrupt: settings.minWordsToInterrupt,
  phonic_enable_assistant_backchannel: settings.enableAssistantBackchannel,
  phonic_assistant_backchannel_aggressiveness: settings.assistantBackchannelAggressiveness,
  phonic_background_noise: settings.backgroundNoise,
  phonic_background_noise_level:
    settings.backgroundNoise === null ? 0 : settings.backgroundNoiseLevel,
  phonic_boosted_keywords: settings.boostedKeywords,
  phonic_pronunciation_dictionary: settings.pronunciationDictionary,
})
